import { Box, Container } from '@material-ui/core';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import Framework, { shapes } from '@greenville/framework';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { getDarkTheme } from './CommonThemes';
import Router from './Router';
import Header from './Header';
import SideDrawer from './SideDrawer';
import Notification from '../../common/components/notification/Notification';
import useContainerStyles from './AIAdminContainerStyles';
import TenantConfigModel from './models/TenantConfig';
import { useTenant } from './components/tenantContext/TenantContext';
import CustomLoader from './common/components/CustomLoader/CustomLoader';
import UnauthorizedBody from '../error/UnauthorizedBody';

const AIAdminContainer = ({ history, language, userPermissions, tenantConfig }) => {
  const classes = useContainerStyles();
  const { tenant, setTenant } = useTenant();
  const location = useLocation();
  const [isUnauthorized, setIsUnauthorized] = React.useState(false);

  const userPermissionDetail = userPermissions?.userPermissionDetail || [];
  const { isSuperAdmin = false, superPermissions = [] } = userPermissionDetail;

  const getTenantPermissions = (id) => {
    const tenantPermissions = userPermissionDetail?.tenantPermissions;
    const permissionList = tenantPermissions?.find((item) => item.tenantId === id);
    return permissionList?.permissions || [];
  };

  const createTenantData = useCallback(
    (tenantId, tenantName, appliedPermissions) => ({
      tenantId,
      isSuperAdmin,
      name: tenantName,
      loginPermissions: appliedPermissions
    }),
    [isSuperAdmin]
  );

  const tenantConfigData = tenantConfig?.data;
  const userTenantPermissionsList = userPermissionDetail?.tenantPermissions;
  const hasTenantPermissions = userTenantPermissionsList && userTenantPermissionsList.length > 0;
  const hasTenantConfigData = tenantConfigData && tenantConfigData.length > 0;

  const { tenantNameFromUrl, tabNameFromUrl } = useMemo(() => {
    const [, , tenantName, tabName] = location.pathname.split('/');
    return { tenantNameFromUrl: tenantName, tabNameFromUrl: tabName };
  }, [location.pathname]);

  // Main tenant initialization effect
  useEffect(() => {
    if (!hasTenantConfigData) {
      return;
    }

    // For non-superAdmins, check if they have tenant permissions
    if (!isSuperAdmin && !hasTenantPermissions) {
      // Non-superAdmin user with no tenant permissions - unauthorized
      setIsUnauthorized(true);
      return;
    }

    // User has valid permissions, clear unauthorized state
    setIsUnauthorized(false);

    const data = tenantConfigData;

    // For superAdmin, use first tenant from config; otherwise use first from permissions
    let defaultTenantId;
    if (isSuperAdmin) {
      defaultTenantId = data[0]?.tenantId;
    } else {
      const initialTenantConfig = userPermissionDetail.tenantPermissions[0];
      defaultTenantId = initialTenantConfig.tenantId;
    }

    const resolveById = (id) => data.find((t) => t.tenantId === id);
    const findTenantByName = (name) => data.find((t) => t.name === name);
    const findTabByName = (ten, tabName) =>
      ten?.tabDetails?.find((t) => {
        const pathRoot = String(t.path || '')
          .replace(/^\//, '')
          .split('/')[0];
        const tabUrlRoot = String(t.tabUrl || '').split('/')[0];
        return t.tabTitle === tabName || t.tabUrl === tabName || tabUrlRoot === tabName || pathRoot === tabName;
      });

    const defaultTenant = resolveById(defaultTenantId);
    let activeTenant = findTenantByName(tenantNameFromUrl);
    let activeTabName;

    const isUrlTenantValid = Boolean(activeTenant);
    let needsRedirect = false;

    if (!isUrlTenantValid) {
      // Tenant from URL doesn't exist in config at all
      activeTenant = defaultTenant;
      needsRedirect = true;
    } else if (!isSuperAdmin) {
      const userHasAccessToTenant = userTenantPermissionsList?.some((tp) => tp.tenantId === activeTenant.tenantId);

      if (!userHasAccessToTenant) {
        activeTenant = defaultTenant;
        needsRedirect = true;
      }
    }

    // Perform redirect if needed
    if (needsRedirect && activeTenant?.name) {
      history.push(`/aiadmin/${activeTenant.name}`);
    }

    // Don't proceed if we don't have a valid activeTenant
    if (!activeTenant) {
      return;
    }

    if (!needsRedirect && tabNameFromUrl) {
      const activeTab = findTabByName(activeTenant, tabNameFromUrl);
      if (activeTab) {
        activeTabName = activeTab.tabTitle || activeTab.tabUrl || activeTab.tabName;
      }
    }

    const tenantPermissions = getTenantPermissions(activeTenant.tenantId);
    const appliedPermissions = toJS(isSuperAdmin ? superPermissions : tenantPermissions);

    setTenant({
      ...activeTenant,
      tenantId: activeTenant.tenantId,
      tenantName: activeTenant.name,
      tenantTitle: activeTenant.title,
      tabDetails: toJS(activeTenant.tabDetails),
      defaultAllBooksType: activeTenant.defaultAllBooksType,
      defaultFeedbackAllBooksType: activeTenant.defaultFeedbackAllBooksType,
      defaultEntityTypes: activeTenant.defaultEntityTypes,
      loginPermissions: appliedPermissions,
      currentTabName: activeTabName
    });

    const tenantData = createTenantData(activeTenant.tenantId, activeTenant.name, appliedPermissions);
    userPermissions.setSelectedPermissions(activeTenant.tenantId);
    Framework.getStoreRegistry().getStore('tenantData').setResponse(tenantData);
  }, [
    hasTenantConfigData,
    hasTenantPermissions,
    userTenantPermissionsList,
    tenantNameFromUrl,
    tabNameFromUrl,
    isSuperAdmin,
    superPermissions,
    createTenantData,
    setTenant,
    history,
    userPermissions
  ]);

  const handleTenantChange = useCallback(
    (params, options = {}) => {
      const { name, tenantId, title, defaultEntityTypes } = params;
      userPermissions.setSelectedPermissions(tenantId);
      const tenantPermissions = getTenantPermissions(tenantId);
      const appliedPermissions = toJS(isSuperAdmin ? superPermissions : tenantPermissions);

      setTenant((prev) => ({
        ...prev,
        ...params,
        tenantName: name,
        tenantTitle: title,
        defaultEntityTypes,
        tabDetails: toJS(params.tabDetails),
        loginPermissions: appliedPermissions,
        currentTabName: options.currentTabName || ''
      }));

      Framework.getStoreRegistry().getStore('userAuditRequest').resetStateValues();

      const tenantData = createTenantData(tenantId, name, appliedPermissions);
      Framework.getStoreRegistry().getStore('tenantData').setResponse(tenantData);
    },
    [userPermissions, isSuperAdmin, superPermissions, setTenant, createTenantData]
  );

  const shouldShowSideDrawer = useCallback(() => {
    return Boolean(tenant?.currentTabName);
  }, [tenant?.currentTabName]);

  const convertedUserPermissionDetail = toJS(userPermissions?.userPermissionDetail);
  const convertedSelectedPermissions = toJS(userPermissions.selectedPermissions) || [];

  const darkTheme = useMemo(() => getDarkTheme(), []);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box className={classes.mainSection} sx={{ backgroundColor: darkTheme.palette.background.paper }}>
        <Notification />
        <Header
          onTenantChange={handleTenantChange}
          userPermissionDetail={convertedUserPermissionDetail}
          isSuperAdmin={isSuperAdmin}
          tenantList={toJS(tenantConfigData)}
        />
        <main id="mainContent" role="main" className={classes.mainContainer}>
          {isUnauthorized && <UnauthorizedBody />}
          {!isUnauthorized && Boolean(tenantConfigData?.length) && Boolean(tenant?.tenantId) && (
            <>
              <Box sx={{ flexGrow: 1, paddingTop: 3 }}>
                {shouldShowSideDrawer() && <SideDrawer loginPermissions={convertedSelectedPermissions} />}
              </Box>
              <Container maxWidth="xl">
                <Router
                  {...{ history, language, userPermissions }}
                  isSuperAdmin={isSuperAdmin}
                  loginPermissions={convertedSelectedPermissions}
                />
              </Container>
            </>
          )}
          {!isUnauthorized && (!tenantConfigData?.length || !tenant?.tenantId) && (
            <CustomLoader size={40} color="primary" thickness={3} label="Loading..." />
          )}
        </main>
      </Box>
    </ThemeProvider>
  );
};

AIAdminContainer.propTypes = {
  history: PropTypes.object.isRequired,
  language: PropTypes.object.isRequired,
  userPermissions: PropTypes.object.isRequired,
  tenantConfig: shapes.modelOf(TenantConfigModel).isRequired
};

export default inject('language', 'userPermissions', 'tenantConfig')(observer(AIAdminContainer));
